/**
 * Onde se fecha um passo de frente (`TP-044`).
 *
 * Fica no DETALHE do Caso, e não no quadro, porque é aqui que há trilha de
 * quem fez o quê — `BR-007` §1.2. O quadro é visão; a ação acontece onde o
 * rastro existe.
 *
 * **Estilo:** MUI e Tailwind com tokens, como o resto do detalhe (`ADR-0005`).
 * As classes `anery-frente*` do protótipo NÃO servem aqui — elas são
 * escopadas sob `.anery-marca`, que é o wrapper da página do protótipo.
 * `ChkRow` serve, porque `.anery-chk` é global.
 *
 * **Forma do cartão:** cabeçalho em `--chao-2` (o mesmo chão da ficha do Caso)
 * e, embaixo, duas colunas que respondem a duas perguntas diferentes: o que
 * fazer AGORA (o passo da vez) e o que precisa CHEGAR (os documentos, `TP-057`).
 * Frente sem documento usa a largura toda. A borda é `--fio`, o fio da marca.
 */

import MoreVertOutlined from '@mui/icons-material/MoreVertOutlined';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { RAIZ_DA_FILA_DA_AVALIADORA } from '../../shared/filaDaAvaliadora/vencidos';
import { BarraDaTrilha, ChkRow, PontoDaTrilha } from '../../shared/ui/primitivas';
import {
  ROTULO_DO_TURNO,
  TURNOS,
  acionamentoDoCaso,
  chavesDaFilaDaAvaliadora,
  type Turno,
} from '../../shared/filaDaAvaliadora/acionamento';
import { textoDoAcionamento } from '../../shared/filaDaAvaliadora/textos';
import { trilhasDoCaso } from './leitura';
import { ROTULO_DO_CAMPO } from './vocabulario';
import { dispensarFrente, fecharPasso } from './api/escritas';
import { documentosDoCaso } from './documentos/api';
import { AceiteSemProva } from './completude/AceiteSemProva';
import { completudeDoCaso } from './completude/api';
import {
  ROTULO_DO_ESTADO,
  idDaFrente,
  provavel,
  textoDaSugestao,
  textoDeFaltaDesde,
  textoDoMotivo,
  type FrenteDaCompletude,
  type ItemDaCompletude,
} from './completude/tipos';
import { DocumentosDaFrente } from './documentos/DocumentosDaFrente';
import { DocumentosSoltos } from './documentos/DocumentosSoltos';
import type { EstadoDaTrilha } from '../../shared/ui/tiposDaEsteira';
import type { CasoDetalhado, FrenteLida } from './api/tipos';

/**
 * O gesto, na língua da operação.
 *
 * Não é decoração: ele diz o PESO do que o botão faz. Aprovar um envio é mais
 * grave que conferir, e decidir não é nenhum dos dois.
 */
const PESO_DO_GESTO: Record<string, string> = {
  confirma: 'confere',
  aprova_envio: 'aprova o envio',
  decide: 'decide',
  automatico: 'automático, visível',
};

/** O rótulo pequeno em caixa alta que abre cada coluna — o mesmo `dt` da ficha do Caso. */
const ROTULO_DE_COLUNA = 'm-0 mb-2 text-[0.6875rem] uppercase tracking-[0.07em] text-[var(--tinta-3)]';

export function PainelDasFrentes({
  caso,
  aoAtualizar,
}: {
  caso: CasoDetalhado;
  aoAtualizar: (atualizado: CasoDetalhado) => void;
}): React.JSX.Element | null {
  const frentes = caso.frentes;
  const lidas = trilhasDoCaso(frentes);
  if (!frentes?.curado || lidas === null) return null;

  return (
    <div className="space-y-3">
      {/* A trava vem antes de tudo: é a resposta de "de quem é a bola". */}
      {lidas.trava !== undefined && (
        <p className="m-0 rounded-md border border-solid border-[var(--fio)] bg-[var(--chao-2)] px-3 py-2 text-[0.8125rem]">
          <span className="text-[var(--tinta-2)]">Trava: </span>
          <b>{lidas.rotulosLongos[lidas.trava.trilha] ?? lidas.trava.trilha}</b>
          <span className="text-[var(--tinta-2)]"> · {lidas.trava.motivo}</span>
        </p>
      )}
      {frentes.semTravaInterna && (
        <p className="m-0 rounded-md border border-solid border-[var(--fio)] bg-[var(--chao-2)] px-3 py-2 text-[0.8125rem] text-[var(--tinta-2)]">
          Nenhuma frente pode andar: todas esperam outra. Não há trava interna.
        </p>
      )}

      {frentes.frentes.map((f) => (
        <CartaoDaFrente
          key={f.frente}
          caso={caso}
          frente={f}
          estado={lidas.trilhas[f.frente]}
          aoAtualizar={aoAtualizar}
        />
      ))}

      {/* Os documentos que chegaram e ainda não sustentam nenhum item (TP-057). */}
      <DocumentosSoltos casoId={caso.id} />
    </div>
  );
}

/**
 * O resumo da frente pela completude (`TP-058`, `D16`): o passo da vez de quantos — o traço amarelo da barra no topo
 * do cartão — e os itens que ainda faltam.
 */
function resumoDaFrente(f: FrenteDaCompletude): string {
  const partes = [`passo ${String(f.passoDaVez)} de ${String(f.totalDePassos)}`];
  if (f.itensFaltando > 0) partes.push(f.itensFaltando === 1 ? 'falta 1 item' : `faltam ${String(f.itensFaltando)} itens`);
  return partes.join(' · ');
}

/**
 * Um item do passo que se PROVA pelo dado, documento ou vínculo (`TP-058`, `D10`): mostra o estado da completude, sem
 * caixinha. A caixinha não o fecha, e oferecê-la seria pedir à pessoa que afirme o que o sistema confere.
 */
function ItemProvavel({ casoId, item }: { casoId: string; item: ItemDaCompletude }): React.JSX.Element {
  const motivo = item.pendente
    ? [textoDoMotivo(item.motivo), item.faltaDesde !== null ? textoDeFaltaDesde(item.faltaDesde) : null]
        .filter((t) => t !== null)
        .join(' · ') || null
    : null;
  return (
    <div>
      <ChkRow
        ok={!item.pendente}
        duvida={item.estado === 'diverge' || item.estado === 'fabricado'}
        texto={
          <>
            {item.rotulo} <span className="text-[var(--tinta-2)]">· {ROTULO_DO_ESTADO[item.estado]}</span>
            {motivo !== null && <span className="block text-[0.75rem] text-[var(--tinta-2)]">{motivo}</span>}
          </>
        }
      />
      {item.pendente && (
        <div className="pb-1 pl-6">
          <AceiteSemProva casoId={casoId} chave={item.chave} />
        </div>
      )}
    </div>
  );
}

/**
 * Um item clínico do passo (`TP-060`): o que o e-mail e os documentos validados dizem aparece como sugestão, e só a
 * caixinha o confere. A sugestão nunca marca o item sozinha (`NR-73`). O que não veio (o CID, muitas vezes) se aceita
 * sem prova, com motivo, em vez de marcar que se conferiu o que não existe.
 */
function ItemSugerido({
  casoId,
  item,
  marcado,
  alternar,
}: {
  casoId: string;
  item: ItemDaCompletude;
  marcado: boolean;
  alternar: () => void;
}): React.JSX.Element {
  const situacao =
    item.estado === 'declarado'
      ? item.caixinhaPor !== null
        ? `conferido por ${item.caixinhaPor}`
        : 'conferido'
      : marcado
        ? 'marcado como conferido'
        : item.pendente
          ? item.faltaDesde !== null
            ? `falta conferir · ${textoDeFaltaDesde(item.faltaDesde).replace(/^falta /, '')}`
            : 'falta conferir'
          : ROTULO_DO_ESTADO[item.estado];
  return (
    <div>
      <button
        type="button"
        className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left"
        aria-pressed={marcado}
        onClick={alternar}
      >
        <ChkRow
          ok={marcado || !item.pendente}
          texto={
            <>
              {item.rotulo} <span className="text-[var(--tinta-2)]">· {situacao}</span>
            </>
          }
        />
      </button>
      {item.sugestoes.length > 0 && (
        <ul
          aria-label={`O que veio para ${item.rotulo}`}
          className="m-0 list-none pb-1 pl-6 pr-0 pt-0 text-[0.75rem] text-[var(--tinta-2)]"
        >
          {item.sugestoes.map((s) => (
            <li key={textoDaSugestao(s)}>{textoDaSugestao(s)}</li>
          ))}
        </ul>
      )}
      {item.pendente && !marcado && (
        <div className="pb-1 pl-6">
          <AceiteSemProva casoId={casoId} chave={item.chave} />
        </div>
      )}
    </div>
  );
}

function CartaoDaFrente({
  caso,
  frente,
  estado,
  aoAtualizar,
}: {
  caso: CasoDetalhado;
  frente: FrenteLida;
  /** O MESMO semáforo e a MESMA barra do cartão no quadro — uma figura só. */
  estado: EstadoDaTrilha | undefined;
  aoAtualizar: (atualizado: CasoDetalhado) => void;
}): React.JSX.Element {
  // Uma chave conferida é uma chave MARCADA. O valor nunca sai do IW, e por
  // isso aqui não há campo de texto — há caixa de seleção (restrição 6).
  const [conferidos, setConferidos] = useState<readonly string[]>([]);
  const [motivo, setMotivo] = useState('');
  const [dispensando, setDispensando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [ancora, setAncora] = useState<HTMLElement | null>(null);
  // O passo com marco da fila da avaliadora (TP-065): quem acionou anota a avaliadora; quem registra o aceite, o turno.
  const [avaliadora, setAvaliadora] = useState('');
  const [turno, setTurno] = useState<Turno | null>(null);
  const clienteDeQuery = useQueryClient();

  // Só a frente de avaliação tem acionamento: as outras não perguntam.
  const ehAvaliacao = frente.frente === 'avaliacao';
  const acionamento = useQuery({ ...acionamentoDoCaso(caso.id), enabled: ehAvaliacao });

  // A mesma consulta que a coluna de documentos usa (fica em cache): aqui só decide se a coluna existe.
  const documentos = useQuery(documentosDoCaso(caso.id));
  const temDocumentos = (documentos.data?.exigidos ?? []).some((i) => i.frente === frente.frente);

  // A completude verificada (TP-058): a MESMA conta da trava do passo e do painel "O que falta para completar".
  const completude = useQuery(completudeDoCaso(caso.id));
  const daFrente = completude.data?.frentes.find((f) => f.frente === frente.frente) ?? null;
  const itemDoCampo = (chave: string): ItemDaCompletude | undefined =>
    completude.data?.itens.find((i) => !i.passo && i.chavesDaCuradoria.includes(chave));

  const passo = frente.passo;
  const encerrada = frente.estado !== 'aberta';

  const agir = (acao: Promise<CasoDetalhado>): void => {
    setOcupado(true);
    setErro(null);
    acao
      .then((atualizado) => {
        setConferidos([]);
        setMotivo('');
        setDispensando(false);
        setAvaliadora('');
        setTurno(null);
        aoAtualizar(atualizado);
        if (ehAvaliacao) {
          void clienteDeQuery.invalidateQueries({ queryKey: chavesDaFilaDaAvaliadora.doCaso(caso.id) });
          void clienteDeQuery.invalidateQueries({ queryKey: RAIZ_DA_FILA_DA_AVALIADORA });
        }
      })
      // A recusa do back é a mensagem que a pessoa lê. Ela NOMEIA o que falta,
      // e reescrevê-la aqui perderia justamente isso.
      .catch((causa: unknown) => {
        setErro(causa instanceof Error ? causa.message : 'Não foi possível concluir.');
      })
      .finally(() => {
        setOcupado(false);
      });
  };

  return (
    <section
      id={idDaFrente(frente.frente)}
      // A barra do topo é fixa: sem a margem, o "ir para a frente" para com o cabeçalho do cartão debaixo dela.
      className="scroll-mt-20 overflow-hidden rounded-lg border border-solid border-[var(--fio)] bg-[var(--chao)]"
      aria-label={`Frente ${frente.rotulo}`}
    >
      {/* Semáforo, rótulo, barra e idade — a mesma figura do cartão no quadro.
          Quem varre o quadro e abre o Caso tem que ver a mesma coisa; se
          discordarem, ninguém confia em nenhuma das duas. */}
      <header className="flex items-center gap-3 border-0 border-b border-solid border-[var(--fio)] bg-[var(--chao-2)] px-4 py-2">
        {estado !== undefined && <PontoDaTrilha estado={estado} />}
        <h3 className="m-0 min-w-0 flex-1 truncate text-sm font-semibold text-[var(--tinta)]">{frente.rotulo}</h3>
        {estado !== undefined && (
          <div className="hidden w-32 sm:block">
            <BarraDaTrilha estado={estado} nome={frente.rotulo} />
          </div>
        )}
        <span className="num shrink-0 text-[0.75rem] tabular-nums text-[var(--tinta-2)]">
          {frente.passosFeitos >= frente.totalDePassos ? '✓' : (estado?.idade ?? '')}
        </span>
        {/* Dispensar a frente é a exceção, não o gesto do dia: mora no menu do cabeçalho, e não solto no pé do cartão. */}
        {!encerrada && (
          <>
            <IconButton
              size="small"
              aria-label={`Mais ações da frente ${frente.rotulo}`}
              aria-haspopup="menu"
              disabled={ocupado || dispensando}
              onClick={(e) => {
                setAncora(e.currentTarget);
              }}
            >
              <MoreVertOutlined fontSize="small" />
            </IconButton>
            <Menu
              anchorEl={ancora}
              open={ancora !== null}
              onClose={() => {
                setAncora(null);
              }}
            >
              <MenuItem
                onClick={() => {
                  setAncora(null);
                  setDispensando(true);
                }}
              >
                Dispensar esta frente
              </MenuItem>
            </Menu>
          </>
        )}
      </header>

      {/* O motivo da dispensa abre logo abaixo do cabeçalho, onde o gesto começou. */}
      {!encerrada && dispensando && (
        <div className="space-y-2 border-0 border-b border-solid border-[var(--fio)] px-4 py-3">
          <p className="m-0 text-[0.8125rem] text-[var(--tinta-2)]">
            Sem motivo, dispensar e não fazer ficam iguais no histórico.
          </p>
          <TextField
            size="small"
            fullWidth
            autoFocus
            label="Motivo"
            value={motivo}
            onChange={(e) => {
              setMotivo(e.target.value);
            }}
          />
          <div className="flex gap-2">
            <Button
              variant="outlined"
              size="small"
              color="warning"
              disabled={ocupado || motivo.trim() === ''}
              onClick={() => {
                agir(dispensarFrente(caso.id, frente.frente, motivo.trim()));
              }}
            >
              Frente dispensada
            </Button>
            <Button
              variant="text"
              size="small"
              onClick={() => {
                setDispensando(false);
                setMotivo('');
              }}
            >
              Cancelar
            </Button>
          </div>
        </div>
      )}

      <div className={temDocumentos ? 'grid md:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]' : ''}>
        <div className="space-y-2 px-4 py-3 text-[0.8125rem]">
          <p className={ROTULO_DE_COLUNA}>{passo !== null ? 'Passo da vez' : 'Situação'}</p>
          {daFrente !== null && !encerrada && (
            <p className="m-0 text-[0.75rem] text-[var(--tinta-2)]">{resumoDaFrente(daFrente)}</p>
          )}

          {frente.estado === 'dispensada' && (
            <p className="m-0 text-[var(--tinta-2)]">Dispensada: {frente.motivoDaDispensa}</p>
          )}

          {frente.estado === 'concluida' && <p className="m-0 text-[var(--tinta-2)]">Concluída.</p>}

          {/* O aceite da avaliadora (TP-065): o prazo, o vencido ou o turno. Vencido em destaque: é a cobrança. */}
          {ehAvaliacao && !encerrada && acionamento.data != null && (
            <p
              className={`m-0 text-[0.8125rem] ${
                acionamento.data.situacao === 'aceite_vencido'
                  ? 'font-semibold text-[var(--roxo)]'
                  : 'text-[var(--tinta-2)]'
              }`}
            >
              {textoDoAcionamento(acionamento.data)}
            </p>
          )}

          {/* Espera outra frente: diz O QUE espera e POR QUÊ, e NÃO oferece o
              botão. Oferecer o gesto a quem não pode dá-lo é o jeito mais rápido
              de a tela perder credibilidade. */}
          {frente.esperando !== null && (
            <p className="m-0 text-[var(--tinta-2)]">
              Espera <b>{frente.esperando}</b>: {frente.porQueEspera}. Anda sozinho quando ela chegar lá.
            </p>
          )}

          {passo !== null && (
            <>
              <div>
                <p className="m-0 text-sm font-semibold text-[var(--tinta)]">{passo.rotulo}</p>
                <p className="m-0 text-[0.75rem] text-[var(--tinta-2)]">
                  {passo.responsavel} · {PESO_DO_GESTO[passo.gesto] ?? passo.gesto}
                </p>
              </div>

              {passo.acao !== '' && <p className="m-0 text-[var(--tinta-2)]">{passo.acao}</p>}

              {passo.campos.length > 0 && (
                <div>
                  {passo.campos.map((chave) => {
                    const item = itemDoCampo(chave);
                    if (item !== undefined && provavel(item)) return <ItemProvavel key={chave} casoId={caso.id} item={item} />;
                    const marcado = conferidos.includes(chave);
                    const alternar = (): void => {
                      setConferidos((atual) => (marcado ? atual.filter((c) => c !== chave) : [...atual, chave]));
                    };
                    if (item?.prova === 'sugerido') {
                      return (
                        <ItemSugerido key={chave} casoId={caso.id} item={item} marcado={marcado} alternar={alternar} />
                      );
                    }
                    return (
                      <button
                        key={chave}
                        type="button"
                        className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left"
                        aria-pressed={marcado}
                        onClick={alternar}
                      >
                        <ChkRow texto={ROTULO_DO_CAMPO[chave] ?? chave} ok={marcado} />
                      </button>
                    );
                  })}
                </div>
              )}

              {passo.marco === 'avaliadora_acionada' && (
                <TextField
                  size="small"
                  fullWidth
                  label="Avaliadora (opcional)"
                  helperText="Quem foi acionada, para a coordenação cobrar o aceite."
                  value={avaliadora}
                  slotProps={{ htmlInput: { maxLength: 120 } }}
                  onChange={(e) => {
                    setAvaliadora(e.target.value);
                  }}
                />
              )}

              {passo.marco === 'avaliadora_aceitou' && (
                <div role="group" aria-label="Turno da visita">
                  <p className="m-0 mb-1 text-[0.75rem] text-[var(--tinta-2)]">Turno da visita</p>
                  <div className="flex gap-2">
                    {TURNOS.map((t) => (
                      <Button
                        key={t}
                        size="small"
                        variant={turno === t ? 'contained' : 'outlined'}
                        aria-pressed={turno === t}
                        onClick={() => {
                          setTurno(t);
                        }}
                      >
                        {ROTULO_DO_TURNO[t]}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-1">
                <Button
                  variant="contained"
                  size="small"
                  // O aceite sem turno não diz quando ela vai: o botão espera a escolha, e o back recusa do mesmo jeito.
                  disabled={ocupado || (passo.marco === 'avaliadora_aceitou' && turno === null)}
                  onClick={() => {
                    agir(
                      fecharPasso(caso.id, frente.frente, passo.ordem, conferidos, {
                        avaliadora:
                          passo.marco === 'avaliadora_acionada' && avaliadora.trim() !== '' ? avaliadora.trim() : undefined,
                        turno: passo.marco === 'avaliadora_aceitou' && turno !== null ? turno : undefined,
                      }),
                    );
                  }}
                >
                  {passo.botao}
                </Button>
              </div>
            </>
          )}

          {erro !== null && (
            <p role="alert" className="m-0 text-[0.8125rem] text-[var(--roxo)]">
              {erro}
            </p>
          )}
        </div>

        {/* Os documentos que os passos desta frente exigem: estado, leitura e ações (TP-057). */}
        {temDocumentos && (
          <div className="border-0 border-t border-solid border-[var(--fio)] px-4 py-3 md:border-l md:border-t-0">
            <DocumentosDaFrente casoId={caso.id} frente={frente.frente} />
          </div>
        )}
      </div>
    </section>
  );
}
